// [claude-code 2026-04-16] S16-T3: Risk Signal cards — full-border severity coloring, solvys-feels polish
// [claude-code 2026-05-03] Solvys cleanup: chevron rows + fading rulers, no card borders.
import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, ShieldAlert } from "lucide-react";
import {
  emptyCachedSignals,
  formatAge,
  isFreshGenerated,
  type CachedSignals,
  type RiskSignal,
  type RiskSignalPayload,
} from "./risk-signal-card-utils";
import { RiskSignalCardRow } from "./RiskSignalCardRow";
import { useRiskSignalDrift } from "./useRiskSignalDrift";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";
const CACHE_KEY = "fintheon:risk-signals";
const POLL_INTERVAL = 120_000;
const LEGACY_FALLBACK_COPY =
  "Recent RiskFlow input cleared the signal threshold before the AI refinement layer produced a card.";

function displayAnalysis(signal: RiskSignal): string {
  if (signal.analysis.includes(LEGACY_FALLBACK_COPY)) {
    return "Pending Agentic Desk refinement. This catalyst met the desk-watch threshold and needs Herald/CAO synthesis before it should be treated as a full Risk Signal.";
  }
  return signal.analysis;
}

function isPendingRefinement(signal: RiskSignal): boolean {
  return (
    signal.refinementStatus === "pending-refinement" ||
    signal.analysis.includes(LEGACY_FALLBACK_COPY)
  );
}

function loadCached(): CachedSignals {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) throw new Error("empty");
    const parsed = JSON.parse(raw);
    const signals = Array.isArray(parsed) ? parsed : (parsed.signals ?? []);
    const generatedAt =
      (Array.isArray(parsed) ? signals[0]?.generatedAt : parsed.generatedAt) ??
      signals[0]?.generatedAt ??
      null;
    return {
      signals,
      generatedAt,
      stale: !isFreshGenerated(generatedAt) || Boolean(parsed.stale),
      freshnessStatus: parsed.freshnessStatus ?? "fresh",
      inputCounts: parsed.inputCounts,
      diagnostics: parsed.diagnostics,
    };
  } catch {
    return emptyCachedSignals();
  }
}

function saveCached(payload: CachedSignals): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* silent */
  }
}

export function RiskSignalCards({ compact = false }: { compact?: boolean }) {
  const cached = loadCached();
  const [signals, setSignals] = useState<RiskSignal[]>(cached.signals);
  const [freshness, setFreshness] = useState<CachedSignals>(cached);
  const [loading, setLoading] = useState(cached.signals.length === 0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchSignals = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/riskflow/risk-signals`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as RiskSignalPayload;
      const freshItems = data.signals ?? [];
      const staleItems = data.staleSignals ?? [];
      const items = freshItems.length > 0 ? freshItems : staleItems;
      const generatedAt =
        data.generatedAt ?? items[0]?.generatedAt ?? cached.generatedAt;
      const nextFreshness: CachedSignals = {
        signals: items,
        generatedAt,
        stale:
          Boolean(data.stale) ||
          freshItems.length === 0 ||
          !isFreshGenerated(generatedAt),
        freshnessStatus: data.freshnessStatus ?? "empty",
        inputCounts: data.inputCounts,
        diagnostics: data.diagnostics,
      };
      setSignals(items);
      setFreshness(nextFreshness);
      saveCached(nextFreshness);
    } catch (err) {
      console.warn("[RiskSignals] fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Visibility-gated polling via IntersectionObserver (root = scroll container)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Find the nearest scrollable ancestor to use as root
    let scrollRoot: HTMLElement | null = el.parentElement;
    while (scrollRoot) {
      const overflow = getComputedStyle(scrollRoot).overflowY;
      if (overflow === "auto" || overflow === "scroll") break;
      scrollRoot = scrollRoot.parentElement;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry?.isIntersecting ?? false);
      },
      { root: scrollRoot, threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Fetch on mount + poll when visible
  useEffect(() => {
    fetchSignals();
  }, [fetchSignals]);

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (visible) {
      pollRef.current = setInterval(fetchSignals, POLL_INTERVAL);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [visible, fetchSignals]);

  const driftData = useRiskSignalDrift(signals);

  const textSize = compact
    ? "text-[10.5px] max-[767px]:text-[12px]"
    : "text-[11.5px]";
  const titleSize = compact
    ? "text-[11.5px] max-[767px]:text-[13px]"
    : "text-[12.5px]";
  const metaSize = compact
    ? "text-[9px] max-[767px]:text-[10px]"
    : "text-[9.5px]";
  const microSize = compact
    ? "text-[8px] max-[767px]:text-[9px]"
    : "text-[8.5px]";
  const padding = compact
    ? "px-1.5 py-2.5 max-[767px]:px-2 max-[767px]:py-3"
    : "px-2.5 py-3";

  if (loading) {
    return (
      <div ref={containerRef} className="flex items-center justify-center py-6">
        <Loader2 className="w-4 h-4 text-[var(--fintheon-accent)]/40 animate-spin" />
      </div>
    );
  }

  if (signals.length === 0) {
    return (
      <div ref={containerRef} className="text-center py-4">
        <ShieldAlert className="w-4 h-4 text-[var(--fintheon-muted)]/30 mx-auto mb-1" />
        <div className="text-[9px] text-[var(--fintheon-muted)]/40">
          {freshness.freshnessStatus === "generation-error"
            ? "Risk signals unavailable"
            : "No fresh risk signals"}
        </div>
        <div className="mt-1 text-[8px] text-[var(--fintheon-muted)]/30">
          Inputs checked {formatAge(freshness.generatedAt)}
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex flex-col py-1">
      {(freshness.stale || freshness.inputCounts || freshness.diagnostics) && (
        <div className="px-2 pb-1 text-[8px] text-[var(--fintheon-muted)]/35">
          Last generated {formatAge(freshness.generatedAt)}
          {freshness.inputCounts
            ? ` / inputs ${freshness.inputCounts.bulletins}B ${freshness.inputCounts.catalysts}C`
            : ""}
          {freshness.diagnostics?.reason
            ? ` / ${freshness.diagnostics.reason}`
            : ""}
        </div>
      )}
      {signals.map((signal, index) => {
        const expanded = expandedId === signal.id;
        return (
          <RiskSignalCardRow
            key={signal.id}
            signal={signal}
            index={index}
            expanded={expanded}
            freshnessStale={freshness.stale}
            drift={driftData[signal.id]}
            padding={padding}
            metaSize={metaSize}
            microSize={microSize}
            textSize={textSize}
            titleSize={titleSize}
            displayedAnalysis={displayAnalysis(signal)}
            isPendingRefinement={isPendingRefinement(signal)}
            onToggle={() => setExpandedId(expanded ? null : signal.id)}
          />
        );
      })}
    </div>
  );
}
